import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export const Navigation = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems: { label: string; href: string; protected?: boolean }[] = [];

  const handleNavigation = (href: string) => {
    if (href.startsWith('/#')) {
      // Handle anchor links
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(href.substring(2));
          element?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const element = document.getElementById(href.substring(2));
        element?.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    setIsOpen(false);
  };

  const NavItems = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {navigationItems.map((item) => (
        <button
          key={item.href}
          onClick={() => handleNavigation(item.href)}
          className={`${
            mobile 
              ? 'block w-full text-left px-4 py-3 text-lg hover:bg-muted rounded-lg transition-colors' 
              : 'px-4 py-2 hover:text-primary transition-colors'
          } ${location.pathname === item.href ? 'text-primary font-medium' : ''}`}
        >
          {item.label}
        </button>
      ))}
    </>
  );

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex items-center space-x-6">
        <NavItems />
      </nav>

      {/* Mobile Navigation */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild className="md:hidden">
          <Button variant="ghost" size="icon">
            <Menu className="h-6 w-6" />
            <span className="sr-only">{t('nav.openMenu')}</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-[300px] sm:w-[400px]">
          <div className="flex items-center justify-between mb-8">
            <span className="font-semibold text-lg">{t('app.title')}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
          
          <div className="flex flex-col space-y-2">
            <NavItems mobile />
            
            <div className="border-t pt-4 mt-6">
              <select
                aria-label={t('header.language')}
                className="w-full bg-secondary text-secondary-foreground rounded-md px-3 py-2 mb-4"
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
              >
                <option value="es">{t('languages.es')}</option>
                <option value="en">{t('languages.en')}</option>
              </select>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};